import { Image as SiteImage, RichText, Swiper } from 'components/shared';
import { useUI } from 'hooks';
import validateRequired from '../validate-required';
import withPropValidation from '../with-props-validation.tsx';
import PageTabs from './page-tabs';
import richTextOptions from './richTextOptions';

const AdaptiveSwiper = ({ children }) => (
  <>
    <Swiper
      className="relative my-[60px] w-full md:hidden"
      slidesPerView={1.4}
      spaceBetween={30}
      slidesOffsetBefore={60}
      slidesOffsetAfter={60}
      wrapperClass="swiper-wrapper items-center"
      darkButtons
    >
      {children}
    </Swiper>
    <Swiper
      className="relative my-[60px] hidden w-full md:block"
      slidesPerView={3.25}
      spaceBetween={60}
      slidesOffsetBefore={60}
      slidesOffsetAfter={60}
      wrapperClass="swiper-wrapper items-center"
      darkButtons
    >
      {children}
    </Swiper>
  </>
);

const NoSwiper = ({ children }) => (
  <div className="flex flex-col flex-wrap gap-y-[60px] px-[16px] pb-[60px] md:flex-row md:gap-x-[16px]">
    {children}
  </div>
);

const SolidArticle = ({ article, className }) => {
  const { isDesktop } = useUI();

  return (
    <article className={className}>
      <SiteImage
        src={
          isDesktop ? article.image || article.mobileImage : article.mobileImage || article.image
        }
        className="relative h-[337px] w-full md:h-[436px]"
      />
      <div
        className={`mx-auto max-w-[300px] pt-[64px] text-center md:mx-0 md:max-w-[425px] md:pl-[64px] md:text-left ${
          article.title ? 'pt-[64px]' : 'pt-[48px]'
        }`}
      >
        {article.title && (
          <h2 className="m-auto mb-[20px] font-nimbus-sans-extd-d text-[35px] font-bold uppercase leading-[90%]">
            {article.title?.split('<br>').map(line => (
              <>
                {line}
                <br />
              </>
            ))}
          </h2>
        )}
        <div className="font-nimbus-sans-d text-[15px] leading-[1.4]">
          <RichText data={article.article} options={richTextOptions(article.article)} />
        </div>
      </div>
    </article>
  );
};

const Section = props => {
  const {
    data: {
      layout,
      showAsCarousel,
      title,
      articlesCollection: { items },
    },
  } = props;

  const { isDesktop } = useUI();

  if (layout === 'Page Tabs') {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <PageTabs {...props} />;
  }

  const articles = items?.filter(i => !!i);

  const Container = showAsCarousel ? AdaptiveSwiper : NoSwiper;

  return (
    <div className="relative w-full overflow-hidden antialiased">
      {title && (
        <h2 className="relative my-[60px] text-center font-nimbus-sans-extd-d text-[35px] font-bold uppercase leading-[90%]">
          {title}
        </h2>
      )}
      <Container>
        {articles.reduce((acc, article) => {
          if (!showAsCarousel) {
            acc.push(
              <SolidArticle
                key={article.sys.id}
                article={article}
                className={`flex-1 ${
                  articles.length >= 4 ? 'md:min-w-[calc(1300px/4)]' : 'md:min-w-[calc(1300px/3)]'
                }`}
              />
            );
          } else {
            acc.push(
              <div className="relative w-[380px]" key={`${article.sys.id}img`}>
                <SiteImage
                  src={
                    isDesktop
                      ? article.image || article.mobileImage
                      : article.mobileImage || article.image
                  }
                  layout="responsive"
                />
              </div>
            );
            acc.push(
              <div
                className="relative flex h-[100%] w-[380px] flex-col justify-center"
                key={`${article.sys.id}txt`}
              >
                {article.title && (
                  <h2 className="relative mb-[20px] text-left font-nimbus-sans-extd-d text-[30px] font-bold uppercase leading-[90%] md:max-w-[345px] md:text-[35px]">
                    {article.title?.split('<br>').map(line => (
                      <span key={line}>
                        {line}
                        <br />
                      </span>
                    ))}
                  </h2>
                )}
                {article.article && (
                  <div className="relative text-left font-nimbus-sans-d text-[15px] leading-[140%] md:max-w-[345px]">
                    <RichText data={article.article} options={richTextOptions(article.article)} />
                  </div>
                )}
              </div>
            );
          }
          return acc;
        }, [])}
      </Container>
    </div>
  );
};

export default withPropValidation(Section, props =>
  validateRequired({ Name: props?.data?.name, Articles: props?.data?.articlesCollection?.items })
);
